import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { action, computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { TTriState } from '../../../models/TTriState.js';
import { InjectedStyledComponent, ResponsiveComponentBase } from '../../../shared/index.js';
import Checkbox from '../../base/Checkbox/Checkbox.js';
import List from '../../base/List/List.js';
import Translate from '../../base/Translate/Translate.js';
import { DocumentDownload } from '../DocumentDownload/DocumentDownload.js';
import { IDocumentDownloadItem } from '../DocumentDownload/models/IDocumentDownloadItem.js';
import { IDocumentDownloadProps } from '../DocumentDownload/models/IDocumentDownloadProps.js';

import {
	IDocumentDownloadListConfiguration,
	IDocumentDownloadListProps,
	IDocumentDownloadListState,
} from './models/index.js';
import DocumentLabel from './styledComponents/DocumentLabel.js';
import Label from './styledComponents/Label.js';
import Wrapper from './styledComponents/Wrapper.js';
import {
	DOCUMENT_DOWNLOAD_LIST_CATEGORY_KEY,
	DOCUMENT_DOWNLOAD_LIST_COMPONENT_KEY,
} from './DocumentDownloadList.meta.js';

export class DocumentDownloadList extends ResponsiveComponentBase<
	IDocumentDownloadListProps,
	IDocumentDownloadListState,
	IDocumentDownloadListConfiguration
> {
	categoryKey = DOCUMENT_DOWNLOAD_LIST_CATEGORY_KEY;
	componentKey = DOCUMENT_DOWNLOAD_LIST_COMPONENT_KEY;

	constructor(props: IDocumentDownloadListProps) {
		super(props);

		makeObservable(this);
	}

	@action
	handleGroupedItemToggle = (documents: IDocumentDownloadItem[]) => {
		const checkboxValue: TTriState = this.getGroupedCheckboxValue(documents);
		if (checkboxValue === true) {
			documents = this.setDocumentsSelectedValue(documents, false);
		}
		if (checkboxValue === false || checkboxValue === 'half') {
			documents = this.setDocumentsSelectedValue(documents, true);
		}
	};

	setDocumentsSelectedValue = (documents: IDocumentDownloadItem[], selected: boolean) => {
		for (const document of documents) {
			document.selected = selected;
		}
		return documents;
	};

	getGroupedCheckboxValue = (documents: IDocumentDownloadItem[]) => {
		let checkboxValue: TTriState = false;
		if (documents && documents.length > 0) {
			if (documents.length === 1) {
				checkboxValue = !!documents[0].selected;
			} else {
				let trueValues = 0;
				let falseValues = 0;
				for (const document of documents) {
					if (document.selected) {
						trueValues++;
					} else {
						falseValues++;
					}
				}

				if (trueValues === documents.length) {
					checkboxValue = true;
				}
				if (trueValues !== 0 && falseValues !== 0) {
					checkboxValue = 'half';
				}
			}
		}
		return checkboxValue;
	};

	@computed
	get groupedDocumentItems() {
		const {
			groupedDocuments = [],
			onItemToggle,
			onDownloadClick,
			allowSelection,
			showPill,
			hideLabel,
			dropdownLabelType,
			pillProps,
			showCopyButton,
			copyButtonProps,
			useSpinner,
		} = this.props;
		const breakpoints = this.breakpoints;
		const response = [];
		for (const groupedDocument of groupedDocuments) {
			const { label, documents } = groupedDocument;

			const describedById = common.commonUtils.generateGuid();
			let documentLabel;
			if (allowSelection) {
				const value = this.getGroupedCheckboxValue(documents);
				documentLabel = (
					<Checkbox
						labelId={describedById}
						label={label}
						value={value}
						onChange={() => this.handleGroupedItemToggle(documents)}
					/>
				);
			} else {
				documentLabel = (
					<Label id={describedById}>
						<Translate>{label}</Translate>
					</Label>
				);
			}
			const documentDownloadProps: IDocumentDownloadProps = {
				breakpoints,
				onItemToggle,
				onDownloadClick,
				documents,
				allowSelection,
				showPill,
				hideLabel,
				dropdownLabelType,
				pillProps,
				useSpinner,
				describedById,
				showCopyButton,
				copyButtonProps,
				availableLabel: groupedDocument.availableLabel,
				selectionLabel: groupedDocument.selectionLabel,
				ariaLabelPrefix: label,
			};

			const element = [
				<DocumentLabel key="label">{documentLabel}</DocumentLabel>,
				<DocumentDownload key="download" {...documentDownloadProps} />,
			];
			response.push(element);
		}
		return response;
	}

	@computed
	get hasDocuments(): boolean {
		const { groupedDocuments } = this.props;
		return !common.commonUtils.isNullOrEmpty(groupedDocuments);
	}

	render() {
		const { className, heading, hideWhenEmpty } = this.props;
		if (!this.hasDocuments && hideWhenEmpty) {
			return null;
		}
		const items = this.groupedDocumentItems;
		return (
			<Wrapper className={className}>
				<List heading={heading} items={items} itemStyle={'justified'} />
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(DocumentDownloadList),
	DOCUMENT_DOWNLOAD_LIST_CATEGORY_KEY,
	DOCUMENT_DOWNLOAD_LIST_COMPONENT_KEY,
);
